import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';
import { Document, DocumentService } from 'src/app/services/document.service';
import { File } from 'src/app/services/interface';
import { Subscription } from 'rxjs';
import { ReadTrackingPayload } from 'src/app/services/read-tracking.service';

@Component({
  selector: 'mvp-documents-list',
  templateUrl: './documents-list.component.html',
})
export class DocumentsListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() documents: Document[] = [];
  @Input() targetUuid: string | null = null;
  @Input() readTrackingPayload: ReadTrackingPayload = {};
  @ViewChildren('documentItem', { read: ElementRef })
  private documentItems?: QueryList<ElementRef<HTMLElement>>;

  assetUrl = environment.assetUrl;

  private expandedDocuments = new Set<string>();
  private expandedFiles = new Set<string>();
  private documentItemsSubscription?: Subscription;
  private focusTargetHandle: ReturnType<typeof setTimeout> | null = null;
  private lastFocusedTargetUuid: string | null = null;
  private viewInitialized = false;

  constructor(private documentService: DocumentService) { }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.targetUuid) {
      this.lastFocusedTargetUuid = null;
    }

    this.expandTargetDocument();
    this.scheduleTargetFocus();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.documentItemsSubscription = this.documentItems?.changes.subscribe(() => {
      this.scheduleTargetFocus();
    });
    this.scheduleTargetFocus();
  }

  ngOnDestroy(): void {
    this.documentItemsSubscription?.unsubscribe();
    if (this.focusTargetHandle !== null) {
      clearTimeout(this.focusTargetHandle);
    }
  }

  openFile(document: Document, file: File): void {
    let fileUrl: string;
    if (file.url) {
      fileUrl = file.url;
    } else if (Capacitor.getPlatform() === 'android' && file.extension === 'pdf') {
      fileUrl = `${environment.apiHostname}/files/${file.uuid}/view`;
    } else {
      fileUrl = `${environment.assetUrl}/files/${file.uuid}.${file.extension}`;
    }

    this.documentService.consult(document.uuid, file.uuid, this.readTrackingPayload).subscribe({
      error: () => undefined,
    });
    Browser.open({ url: fileUrl });
    return;
  }

  hasFile(document: Document): boolean {
    return !!document && Array.isArray(document.files) && document.files.length > 0;
  }

  hasMultipleFiles(document: Document): boolean {
    return this.hasFile(document) && document.files.length > 1;
  }

  handleDocumentClick(document: Document) {
    if (!this.hasFile(document)) {
      return;
    }
    if (document.files.length === 1) {
      this.openFile(document, document.files[0]);
      return;
    }
    this.toggleFiles(document);
  }

  isExpanded(document: Document): boolean {
    return this.expandedDocuments.has(document.uuid);
  }

  isFilesExpanded(document: Document): boolean {
    return this.expandedFiles.has(document.uuid);
  }

  isTargeted(document: Document): boolean {
    return !!this.targetUuid && document.uuid === this.targetUuid;
  }

  shouldDisplayDocument(document: Document): boolean {
    return this.hasFile(document) || this.isTargeted(document);
  }

  toggleInfo(document: Document, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.expandedDocuments.has(document.uuid)) {
      this.expandedDocuments.delete(document.uuid);
    } else {
      this.expandedDocuments.add(document.uuid);
    }
  }

  toggleFiles(document: Document, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.expandedFiles.has(document.uuid)) {
      this.expandedFiles.delete(document.uuid);
    } else {
      this.expandedFiles.add(document.uuid);
    }
  }

  getLatestFile(document: Document): File | null {
    if (!this.hasFile(document)) {
      return null;
    }

    return document.files.reduce((latest: File | null, file: File) => {
      if (!latest) {
        return file;
      }
      return file.updated_at > latest.updated_at ? file : latest;
    }, null);
  }

  private expandTargetDocument(): void {
    if (!this.targetUuid) {
      return;
    }

    const targetDocument = this.documents.find((document) => document.uuid === this.targetUuid);
    if (!targetDocument) {
      return;
    }

    if (targetDocument.description) {
      this.expandedDocuments.add(targetDocument.uuid);
    }
    if (this.hasFile(targetDocument)) {
      this.expandedFiles.add(targetDocument.uuid);
    }
  }

  private scheduleTargetFocus(): void {
    if (
      !this.viewInitialized ||
      !this.targetUuid ||
      this.lastFocusedTargetUuid === this.targetUuid
    ) {
      return;
    }

    if (this.focusTargetHandle !== null) {
      clearTimeout(this.focusTargetHandle);
    }

    this.focusTargetHandle = setTimeout(() => {
      this.focusTargetHandle = null;
      this.focusTargetDocument();
    }, 0);
  }

  private focusTargetDocument(): void {
    const targetItem = this.documentItems?.find(
      (item) => item.nativeElement.dataset.documentUuid === this.targetUuid
    );
    if (!targetItem) {
      return;
    }

    this.lastFocusedTargetUuid = this.targetUuid;
    if (typeof targetItem.nativeElement.scrollIntoView === 'function') {
      targetItem.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
}
